import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Building2, MapPin, X, Upload } from "lucide-react";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";

export default function AdminChapters() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => { if (user?.role === "admin") refresh(); }, [user]);

  const refresh = async () => {
    try { const { data } = await api.get("/chapters"); setItems(data); } catch { /* silent */ }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-aubergine/20 border-t-aubergine rounded-full animate-spin" /></div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const remove = async (id) => {
    if (!window.confirm("Delete this chapter permanently?")) return;
    try { await api.delete(`/chapters/${id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <DashboardShell role="admin" title="Chapters" subtitle="Add cities, launch new chapters, edit existing ones">
      <div data-testid="admin-chapters" className="space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-plumSoft text-sm">{items.length} chapter{items.length !== 1 && "s"}</p>
          <button data-testid="ac-new" onClick={() => setEditing("new")} className="inline-flex items-center gap-2 bg-aubergine text-ivory px-5 py-2.5 rounded-full text-[12px] uppercase tracking-widest hover:bg-aubergine/90">
            <Plus className="w-4 h-4" /> New Chapter
          </button>
        </div>

        {items.length === 0 && (
          <div className="rounded-2xl bg-white border border-aubergine/[0.08] p-16 text-center">
            <Building2 className="w-10 h-10 text-plumSoft/40 mx-auto mb-3" />
            <p className="font-display text-xl text-aubergine">No chapters yet</p>
            <p className="text-sm text-plumSoft mt-1">Launch your first chapter — it will appear on /chapters immediately.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map(c => (
            <div key={c.id} className="bg-white rounded-2xl border border-aubergine/[0.08] overflow-hidden">
              <div className="aspect-[16/9] bg-lavender/50 relative">
                {c.cover_image && <img src={c.cover_image} alt={c.name} className="w-full h-full object-cover" />}
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 text-[10px] tracking-widest uppercase text-aubergine flex items-center gap-1">
                  <MapPin className="w-3 h-3" />{c.city || "—"}
                </span>
              </div>
              <div className="p-5">
                <h3 className="font-display text-lg text-aubergine leading-snug">{c.name}</h3>
                <p className="text-[11px] text-plumSoft/80 mt-1.5 line-clamp-2">{c.description}</p>
                <p className="text-[11px] text-plumSoft/60 mt-2">
                  <span className="uppercase tracking-widest">{c.founded ? `Founded ${c.founded}` : "New"}</span> · {c.members || 0} members
                </p>
                <div className="mt-4 pt-4 border-t border-aubergine/[0.06] flex items-center justify-end gap-1">
                  <button onClick={() => setEditing(c)} data-testid={`ac-edit-${c.id}`} className="p-2 rounded-lg text-plum hover:bg-aubergine/[0.06]" title="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(c.id)} data-testid={`ac-del-${c.id}`} className="p-2 rounded-lg text-rose hover:bg-rose/[0.06]" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {editing && (
          <ChapterEditor chapter={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
        )}
      </div>
    </DashboardShell>
  );
}

function ChapterEditor({ chapter, onClose, onSaved }) {
  const isNew = !chapter;
  const [form, setForm] = useState({
    name: chapter?.name || "",
    city: chapter?.city || "",
    description: chapter?.description || "",
    cover_image: chapter?.cover_image || "",
    founded: chapter?.founded || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.name.trim() || !form.city.trim()) {
      toast.error("Name and city are required"); return;
    }
    setBusy(true);
    try {
      if (isNew) await api.post("/chapters", form);
      else await api.put(`/chapters/${chapter.id}`, form);
      toast.success(isNew ? "Chapter created" : "Chapter updated");
      onSaved();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-aubergine/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-ivory rounded-2xl w-full max-w-2xl border border-aubergine/15 shadow-2xl my-8">
        <div className="p-6 border-b border-aubergine/[0.08] flex items-center justify-between">
          <h2 className="font-display text-2xl text-aubergine">{isNew ? "New Chapter" : "Edit Chapter"}</h2>
          <button onClick={onClose} className="text-plum hover:text-aubergine p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Chapter name *" testid="ce-name" value={form.name} onChange={(v) => setForm({...form, name: v})} placeholder="e.g. WOWSHE Hyderabad" />
            <Field label="City *" testid="ce-city" value={form.city} onChange={(v) => setForm({...form, city: v})} placeholder="e.g. Hyderabad" />
          </div>
          <Field label="Founded (year or Coming Soon)" testid="ce-founded" value={form.founded} onChange={(v) => setForm({...form, founded: v})} placeholder="e.g. 2024 or Coming Soon" />
          <div>
            <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">Description</label>
            <textarea data-testid="ce-desc" rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-luxe" placeholder="Short description shown on the chapters page" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">Cover image</label>
            <CoverUploader value={form.cover_image} onChange={(url) => setForm({...form, cover_image: url})} />
          </div>
        </div>
        <div className="p-6 border-t border-aubergine/[0.08] flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-[12px] uppercase tracking-widest text-plum hover:text-aubergine">Cancel</button>
          <button onClick={save} data-testid="ce-save" disabled={busy} className="inline-flex items-center gap-2 bg-aubergine text-ivory px-6 py-2.5 rounded-full text-[12px] uppercase tracking-widest hover:bg-aubergine/90 disabled:opacity-50">
            {busy ? "Saving…" : (isNew ? "Create Chapter" : "Save Changes")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">{label}</label>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="input-luxe" />
    </div>
  );
}

function CoverUploader({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type || "")) { toast.error("Please pick an image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };
  const previewSrc = value ? (value.startsWith("http") ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`) : "";
  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      {previewSrc ? (
        <div className="flex items-start gap-4">
          <img src={previewSrc} alt="Cover" className="w-56 aspect-video object-cover rounded-lg border border-aubergine/8" />
          <div className="flex flex-col gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="text-xs text-plum inline-flex items-center gap-1 hover:text-aubergine disabled:opacity-50"><Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Replace"}</button>
            <button type="button" onClick={() => onChange("")} className="text-xs text-rose inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="w-full rounded-xl border-2 border-dashed border-aubergine/20 bg-white/60 px-5 py-8 text-sm text-plumSoft flex flex-col items-center gap-2 hover:border-aubergine/40 disabled:opacity-50">
          <Upload className="w-5 h-5 text-plum" />
          <span>{busy ? "Uploading…" : "Click to upload chapter cover"}</span>
          <span className="text-[10px] uppercase tracking-widest text-plumSoft/60">JPG / PNG / WEBP · up to 5 MB</span>
        </button>
      )}
    </div>
  );
}
